import type { AdminMetricsDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsTile } from './AnalyticsTile';
import { DeltaBadge } from './DeltaBadge';
import { tokens } from '../../../design';
import { buildBars, buildSeriesPath, seriesMax } from '../_lib/chartGeometry';

/**
 * Tile 1 — GMV·7д. Renders the daily gmv / paid / refunds series as inline
 * SVG (area+line for gmv, line for paid, bars for refunds), the total GMV
 * big number and the period-over-period delta badge. Mirrors the `.t-gmv`
 * tile in `design/mockups/admin/dashboard.html`.
 *
 * NO charting dependency — everything is hand-built SVG via the pure
 * `chartGeometry` helpers. A zero-filled series draws a flat baseline.
 */

const VB_W = 600;
const VB_H = 200;

const GMV_NUMBER = new Intl.NumberFormat('ru-BY', { maximumFractionDigits: 0 });

type Gmv = AdminMetricsDto['gmv'];

const LegendItem = ({ color, label }: { color: string; label: string }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '2px', background: color }} />
    {label}
  </Box>
);

const GmvChart = ({ gmv, label }: { gmv: Gmv; label: string }): ReactElement => {
  const max = seriesMax(gmv.series);
  const gmvPath = buildSeriesPath(gmv.series.map((p) => p.gmv), VB_W, VB_H, max);
  const paidPath = buildSeriesPath(gmv.series.map((p) => p.paid), VB_W, VB_H, max);
  const refundBars = buildBars({
    values: gmv.series.map((p) => p.refunds),
    width: VB_W,
    height: VB_H,
    max,
    barWidth: 14,
  });

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      sx={{ mt: 'auto', width: '100%', height: 150, display: 'block' }}
    >
      <defs>
        <linearGradient id="gmv-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={tokens.brand.primary} stopOpacity="0.28" />
          <stop offset="1" stopColor={tokens.brand.primary} stopOpacity="0" />
        </linearGradient>
      </defs>
      <g stroke={tokens.border.default} strokeWidth="1" strokeDasharray="3 4">
        <line x1="0" y1={VB_H * 0.25} x2={VB_W} y2={VB_H * 0.25} />
        <line x1="0" y1={VB_H * 0.5} x2={VB_W} y2={VB_H * 0.5} />
        <line x1="0" y1={VB_H * 0.75} x2={VB_W} y2={VB_H * 0.75} />
      </g>
      <g fill={tokens.state.warning} opacity="0.4">
        {refundBars.map((b, i) =>
          b.height > 0 ? <rect key={i} x={b.x} y={b.y} width={b.width} height={b.height} rx="2" /> : null,
        )}
      </g>
      {gmvPath.area ? <path d={gmvPath.area} fill="url(#gmv-fill)" /> : null}
      <path d={paidPath.line} fill="none" stroke={tokens.state.info} strokeWidth="2" opacity="0.85" />
      <path d={gmvPath.line} fill="none" stroke={tokens.brand.primary} strokeWidth="3" />
    </Box>
  );
};

const GmvBody = ({ gmv, label }: { gmv: Gmv; label: string }): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
        <Box sx={{ fontFamily: tokens.font.mono, fontSize: 42, fontWeight: 700, letterSpacing: '-0.8px' }}>
          {GMV_NUMBER.format(gmv.totalGmv)}
          <Box component="span" sx={{ fontSize: 16, fontWeight: 500, ml: 0.5, color: tokens.text.tertiary }}>
            BYN
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.75, mt: 1, fontSize: 11, color: tokens.text.secondary }}>
        <LegendItem color={tokens.brand.primary} label={t('dashboard.gmv.legend.gmv')} />
        <LegendItem color={tokens.state.info} label={t('dashboard.gmv.legend.paid')} />
        <LegendItem color={tokens.state.warning} label={t('dashboard.gmv.legend.refunds')} />
      </Box>
      <GmvChart gmv={gmv} label={label} />
    </>
  );
};

export interface GmvTileProps {
  data: AdminMetricsDto | undefined;
  isLoading: boolean;
  isError: boolean;
  gridColumn: string;
  gridRow?: string;
}

export const GmvTile = ({
  data,
  isLoading,
  isError,
  gridColumn,
  gridRow,
}: GmvTileProps): ReactElement => {
  const { t } = useTranslation();
  const gmv = data?.gmv;
  const label = t('dashboard.gmv.title');

  return (
    <AnalyticsTile
      label={label}
      gridColumn={gridColumn}
      gridRow={gridRow}
      headerSlot={gmv ? <DeltaBadge deltaPct={gmv.deltaPct} /> : null}
      isLoading={isLoading}
      isError={isError}
      hasData={Boolean(gmv)}
    >
      {gmv ? <GmvBody gmv={gmv} label={label} /> : null}
    </AnalyticsTile>
  );
};
